import { useState, type ReactNode } from "react";
import { PencilIcon } from "lucide-react";

import { SheetCertifications as UserSheetCertifications } from "@/components/user-sheet/profile-editors/SheetCertifications";
import { SheetEducations as UserSheetEducations } from "@/components/user-sheet/profile-editors/SheetEducations";
import { SheetProfessionalSummary as UserSheetProfessionalSummary } from "@/components/user-sheet/profile-editors/SheetProfessionalSummary";
import { SheetSkills as UserSheetSkills } from "@/components/user-sheet/profile-editors/SheetSkills";
import { SheetWorkExperiences as UserSheetWorkExperiences } from "@/components/user-sheet/profile-editors/SheetWorkExperiences";
import { useUserSheet } from "../userSheetContext";
import { withRequiredFields } from "../withRequiredFields";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  RichTextDisplay,
  Row,
  SheetSectionEmptyText,
  SheetSectionHeader,
  Text,
} from "@/components";
import { CertificationSheetContent } from "@ui/profile/certification/CertificationSheetContent";
import { EducationSheetContent } from "@ui/profile/education/EducationSheetContent";
import { SkillsSheetContent } from "@ui/profile/skills/SkillsSheetContent";
import { WorkExperienceSheetContent } from "@ui/profile/workExperience/WorkExperienceSheetContent";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { cn } from "@/lib/utils";
import type { ClassifiedKeyword } from "@/types";

export const ProfileTab = ({
  execSummary,
  headerCard,
  highlightKeywords = [],
  isEditable = false,
  professionalSummaryAction,
  rail,
  showProfessionalSummary = false,
}: {
  // Consumer-supplied executive summary section; candidacy-level, so it renders
  // above the profile body only on candidacy-backed sheets.
  execSummary?: ReactNode;
  // Consumer-supplied card pinned above everything (e.g. the search sheet's AI
  // summary). Null/undefined renders nothing.
  headerCard?: ReactNode;
  highlightKeywords?: ClassifiedKeyword[];
  isEditable?: boolean;
  // Consumer-supplied action pinned to the right of the summary header.
  professionalSummaryAction?: ReactNode;
  // Consumer-supplied right rail (e.g. <UserSheetActivityRail />). Owns its own width.
  rail?: ReactNode;
  showProfessionalSummary?: boolean;
}) => {
  /* Context */
  const { contentPaddingClassName, jobId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });

  /* React Hooks */
  const [selectedSection, setSelectedSection] = useState<string>();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /* Helpers */
  const profile = userApi.data;
  const isEditing = !!selectedSection;
  const editingWorkExperienceId = selectedSection?.startsWith(
    "work-experience-",
  )
    ? Number(selectedSection.replace("work-experience-", ""))
    : undefined;

  /* Handlers */
  const handleEditSection = (section: string) => {
    setSelectedSection(section);
    setHasUnsavedChanges(false);
  };

  // Mutation hooks write edits into the sheet-data cache in place, so leaving
  // edit mode never refetches.
  const handleDoneEditing = () => {
    setSelectedSection(undefined);
    setHasUnsavedChanges(false);
  };

  const handleCancelEditing = () => {
    setSelectedSection(undefined);
    setHasUnsavedChanges(false);
  };

  /* Render */
  const renderSectionEditButton = (section: string, label: string) => {
    if (!isEditable || isEditing) return null;

    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-6 w-6 opacity-0 transition-opacity duration-150 group-hover/section:opacity-100 group-hover/section:delay-200"
              onClick={() => {
                handleEditSection(section);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <PencilIcon
                className="h-4 w-4 text-slate-400"
                strokeWidth={1.5}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderEditFooter = (section: string) => {
    const isActiveSection =
      selectedSection === section ||
      (section === "work-experience" &&
        selectedSection?.startsWith("work-experience-"));

    if (!isActiveSection) return null;

    return (
      <Row className="items-center justify-end gap-3 pb-6 pt-4">
        {hasUnsavedChanges ? (
          <Text className="text-xs font-medium text-slate-500">
            Unsaved changes
          </Text>
        ) : null}
        <Button
          data-inline-editor-active
          onClick={handleCancelEditing}
          size="sm"
          type="button"
          variant="secondary"
        >
          Cancel
        </Button>
        <Button onClick={handleDoneEditing} size="sm" type="button">
          Save
        </Button>
      </Row>
    );
  };

  const renderProfessionalSummary = () => {
    if (!profile) return null;
    if (!showProfessionalSummary) return null;

    if (selectedSection === "summary") {
      return (
        <Column
          className="gap-0"
          onChange={() => {
            setHasUnsavedChanges(true);
          }}
          onInput={() => {
            setHasUnsavedChanges(true);
          }}
        >
          <UserSheetProfessionalSummary
            containerClasses="border-none p-0"
            isEditing
            professionalSummary={profile.professionalSummary}
            userId={workerId}
          />
          {renderEditFooter("summary")}
        </Column>
      );
    }

    return (
      <Column
        className="group/section gap-4"
        onDoubleClick={() => {
          if (isEditable && !isEditing) handleEditSection("summary");
        }}
      >
        <Row className="items-center justify-between">
          <Row className="items-center gap-1">
            <SheetSectionHeader title="Professional Summary" />
            {renderSectionEditButton("summary", "Edit Professional Summary")}
          </Row>
          {professionalSummaryAction}
        </Row>
        {profile.professionalSummary ? (
          <RichTextDisplay richText={profile.professionalSummary} />
        ) : (
          <SheetSectionEmptyText text="This candidate hasn't added a professional summary." />
        )}
      </Column>
    );
  };

  const renderWorkExperience = () => {
    if (!profile) return null;

    if (isEditable) {
      return (
        <Column
          className="group/section gap-0"
          onChange={() => {
            setHasUnsavedChanges(true);
          }}
          onInput={() => {
            setHasUnsavedChanges(true);
          }}
        >
          <UserSheetWorkExperiences
            editingWorkExperienceFooter={
              editingWorkExperienceId === undefined
                ? null
                : renderEditFooter("work-experience")
            }
            editingWorkExperienceId={editingWorkExperienceId}
            onEditWorkExperience={(workExperienceId) => {
              handleEditSection(`work-experience-${String(workExperienceId)}`);
            }}
            userId={workerId}
            workExperiences={withRequiredFields(profile.workExperience, ["id"])}
          />
        </Column>
      );
    }

    return (
      <Column className="group/section gap-0">
        <WorkExperienceSheetContent
          className={headerCard ? "pt-6" : ""}
          highlightKeywords={highlightKeywords}
          title="Work Experience"
          workExperiences={withRequiredFields(profile.workExperience, ["id"])}
        />
      </Column>
    );
  };

  const renderSkills = () => {
    if (!profile) return null;

    if (isEditable) {
      return (
        <Column
          className="group/section gap-0"
          onChange={() => {
            setHasUnsavedChanges(true);
          }}
          onDoubleClick={() => {
            if (!isEditing) handleEditSection("skills");
          }}
          onInput={() => {
            setHasUnsavedChanges(true);
          }}
        >
          <UserSheetSkills
            isEditing={selectedSection === "skills"}
            skills={withRequiredFields(profile.skills, ["id"])}
            titleActions={renderSectionEditButton("skills", "Edit Skills")}
            userId={workerId}
          />
          {renderEditFooter("skills")}
        </Column>
      );
    }

    return (
      <Column className="gap-0">
        <SkillsSheetContent
          skills={withRequiredFields(profile.skills, ["id"])}
        />
      </Column>
    );
  };

  const renderCertifications = () => {
    if (!profile) return null;

    if (isEditable) {
      return (
        <Column className="group/section gap-0">
          <UserSheetCertifications
            certifications={withRequiredFields(profile.certifications, ["id"])}
            userId={workerId}
          />
        </Column>
      );
    }

    return (
      <Column className="gap-0">
        <CertificationSheetContent
          certifications={withRequiredFields(profile.certifications, ["id"])}
        />
      </Column>
    );
  };

  const renderEducation = () => {
    if (!profile) return null;

    if (isEditable) {
      return (
        <Column className="group/section gap-0">
          <UserSheetEducations
            educations={withRequiredFields(profile.education, ["id"])}
            userId={workerId}
          />
        </Column>
      );
    }

    return (
      <Column className="gap-0">
        <EducationSheetContent
          educations={withRequiredFields(profile.education, ["id"])}
        />
      </Column>
    );
  };

  const render = () => {
    if (userApi.isPending) {
      return (
        <Column className={cn("py-7", contentPaddingClassName)}>
          <LoadingIndicator variant="medium" />
        </Column>
      );
    }
    if (userApi.isError || !profile) {
      return (
        <Column className={cn("py-7", contentPaddingClassName)}>
          <ErrorText errorMessage="Error retrieving profile." />
        </Column>
      );
    }

    return (
      <Row
        className={cn(
          "mb-12 flex-col items-start gap-6 py-6 lg:flex-row",
          contentPaddingClassName,
        )}
      >
        <Column className="min-w-0 flex-1 gap-6">
          {headerCard}
          {execSummary}
          {renderProfessionalSummary()}
          {renderWorkExperience()}
          {renderSkills()}
          {renderCertifications()}
          {renderEducation()}
        </Column>
        {rail}
      </Row>
    );
  };

  return render();
};
